import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API_BASE from "../../config";
import { usePayrollEnabled } from "../../utils/payrollFeature";
import { extractErrorMessage } from "../../utils/apiError";
import RejectReasonDialog from "../RejectReasonDialog";
import styles from "./Topbar.module.css";

/**
 * Multi-tenant school pill — sits in the Topbar to show the active
 * school for branch users, OR a dropdown to switch context for
 * chain admins (SuperAdmin / SuperDev). Reads the session user's
 * `school` (active) + `availableSchools` (selector options), both
 * populated by AuthController.attachSchoolContext on login.
 *
 * On switch: writes the chosen school into sessionStorage as the
 * user's active school, then hard-reloads /dashboard so every cached
 * piece of state is refreshed with the new X-School-Id header. We
 * pick a hard reload over an in-place rerender because too many
 * pages cache lists on mount; a switch must look like "a new
 * session" to every component, otherwise stale data would haunt
 * the UI until the user clicks around.
 */
function SchoolPill({ user, onChanged }) {
  const [open, setOpen] = useState(false);
  // Live list of available schools — fetched on each dropdown open
  // for chain admins so a newly-created branch shows up without
  // forcing a logout/login cycle. Seeded from the cached session
  // (availableSchools[]) so the dropdown has SOMETHING to show
  // during the network round-trip.
  const [liveOptions, setLiveOptions] = useState(() =>
    Array.isArray(user?.availableSchools) ? user.availableSchools : []);
  const [loadingLive, setLoadingLive] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const school = user?.school;
  // Chain-READ tier — can switch branches via the topbar selector.
  // SuperDev, SuperAdmin, and Owner all qualify. Branch admins
  // (Admin, Registrar, Cashier, Teacher, etc.) see a static pill
  // showing their pinned school — no switcher.
  //
  // (SuperDev is additionally the only role that can create / edit
  // schools — that's gated separately in SchoolsPage routing and on
  // the backend's CHAIN_WRITE_ROLES check.)
  const isChainAdmin = user?.role === "SuperDev"
                    || user?.role === "SuperAdmin"
                    || user?.role === "Owner";

  // Chain admins ALWAYS get a clickable pill, even when only one
  // school currently exists — they should be able to open the menu,
  // see the current list, and confirm whether a new branch was just
  // registered. Branch users get a static pill — no dropdown —
  // pinned to their school by design.
  const canSwitch = isChainAdmin;

  // When a chain admin opens the dropdown, refetch the live list of
  // active schools so a newly-added branch appears immediately.
  // Falls back to the cached options on network failure.
  const openMenu = async () => {
    setOpen(o => !o);
    if (!isChainAdmin) return;
    if (open) return;       // already open → this click closes it; skip refetch
    setLoadingLive(true);
    try {
      const r = await fetch(`${API_BASE}/api/schools`);
      if (r.ok) {
        const list = await r.json();
        if (Array.isArray(list)) setLiveOptions(list);
      }
    } catch { /* ignore — keep cached options */ }
    finally { setLoadingLive(false); }
  };

  const switchTo = (s) => {
    if (!s) { setOpen(false); return; }
    if (school && s.id === school.id) { setOpen(false); return; }
    try {
      // Update both possible storages so a "remember me" branch
      // stays in sync. The fetch wrapper reads sessionStorage first,
      // localStorage as fallback.
      ["sessionStorage", "localStorage"].forEach(k => {
        const raw = window[k].getItem("user");
        if (!raw) return;
        const obj = JSON.parse(raw);
        obj.school = s;
        // Also refresh availableSchools cache so a subsequent reload
        // doesn't lose the newly-discovered branch.
        if (liveOptions && liveOptions.length > 0) obj.availableSchools = liveOptions;
        window[k].setItem("user", JSON.stringify(obj));
      });
    } catch { /* ignore */ }
    setOpen(false);
    if (onChanged) onChanged(s);
    // Hard reload — see component-level doc.
    window.location.assign("/dashboard");
  };

  // Don't render the pill at all if there's no school context AND the
  // caller isn't a chain admin (i.e., legacy branch session pre-Phase 2A).
  if (!school && !isChainAdmin) return null;

  // Active school for the pill label. For chain admins on a fresh
  // legacy session (no school field yet) we show the first live
  // option or a placeholder until they pick.
  const active = school || liveOptions[0] || null;
  const label = active?.shortName || active?.name || "Pick a school";

  return (
    <div className={styles.schoolPill} ref={ref}>
      <button
        type="button"
        className={`${styles.schoolPillBtn} ${canSwitch ? styles.schoolPillBtnSwitchable : ""}`}
        onClick={openMenu}
        disabled={!canSwitch}
        title={canSwitch ? "Switch school" : label}
      >
        {active?.logoUrl
          ? <img src={active.logoUrl} alt="" className={styles.schoolPillLogo} />
          : <span className={styles.schoolPillLogoDot} aria-hidden>🏫</span>}
        <span className={styles.schoolPillLabel}>{label}</span>
        {canSwitch && (
          <svg className={`${styles.schoolPillChev} ${open ? styles.schoolPillChevOpen : ""}`}
               width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
            <path d="M2.5 4.5 L6 8 L9.5 4.5" stroke="currentColor"
                  strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>
      {open && canSwitch && (
        <div className={styles.schoolPillMenu} role="menu">
          <div className={styles.schoolPillMenuHead}>
            {loadingLive ? "Loading branches…" : "Switch school context"}
          </div>
          {liveOptions.length === 0 && !loadingLive && (
            <div className={styles.schoolPillMenuItem} style={{ cursor: "default", color: "#8a6d28" }}>
              No active branches found. Go to Schools / Branches to add one.
            </div>
          )}
          {liveOptions.map(s => (
            <button
              key={s.id}
              type="button"
              role="menuitem"
              className={`${styles.schoolPillMenuItem} ${active && s.id === active.id ? styles.schoolPillMenuItemActive : ""}`}
              onClick={() => switchTo(s)}
            >
              {s.logoUrl
                ? <img src={s.logoUrl} alt="" className={styles.schoolPillMenuLogo} />
                : <span className={styles.schoolPillMenuLogoDot} aria-hidden>🏫</span>}
              <span className={styles.schoolPillMenuLabel}>
                {s.shortName || s.name}
                {!s.active && <span className={styles.schoolPillMenuInactive}> · inactive</span>}
              </span>
              {active && s.id === active.id && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.4"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Optional Payroll module nav group — appended to the SuperAdmin's nav
 *  only when Settings → Maintenance has flipped the feature ON. Lives
 *  outside NAV_BY_ROLE because it's a runtime conditional, not a static
 *  per-role list. */
const PAYROLL_NAV_GROUP = { label: "Payroll", items: [
  { label: "Salary",         path: "/salary"            },
  { label: "Timesheet",      path: "/timesheet"         },
  { label: "HR Calendar",    path: "/payroll-calendar"  },
  { label: "Class Schedule", path: "/schedule"          },
]};

function fmtMoney(n) {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtRelative(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1)   return "just now";
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Phase 3+: role-aware nav. An entry is EITHER a flat link
 *   { label, path }
 * OR a dropdown group
 *   { label, items: [{ label, path }, ...] }
 */
/** Site Content dropdown — shared by ContentManager and Admin/SuperAdmin so
 *  the nav matches the backend's edit permission check. */
const SITE_CONTENT_GROUP = { label: "Site Content", items: [
  { label: "Homepage",       path: "/admin/site-content/homepage" },
  { label: "About Us",       path: "/admin/site-content/about" },
  { label: "Programs",       path: "/admin/site-content/programs" },
  { label: "News & Events",  path: "/articles" },
  { label: "Contact Info",   path: "/admin/site-content/contact" },
  { label: "Footer",         path: "/admin/site-content/footer" },
  { label: "Media Library",  path: "/admin/program-images" },
]};

const ADMIN_NAV = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "Academics", path: "/academics" },
  { label: "Admissions", path: "/admissions/new" },
  { label: "People", items: [
    { label: "Students & Parents", path: "/directory" },
    { label: "Staff",              path: "/people" },
  ]},
  { label: "Operations", items: [
    { label: "Tuition",              path: "/admin/tuition" },
    { label: "Expenses",             path: "/admin/expenses" },
    { label: "Statement of Account", path: "/tuition/soa" },
    { label: "Books",                path: "/admin/books" },
    { label: "Facilities",           path: "/admin/facilities" },
    { label: "IT Lab",               path: "/itlab" },
    // Chain-tier entries. The Schools / Branches CRUD is SuperDev-
    // exclusive (`superDevOnly: true`). The Overview dashboard is a
    // chain-READ surface — visible to SuperDev / SuperAdmin / Owner
    // (the same set that can switch branches via the topbar pill).
    // Both tags get filtered out for Admin and lower at render time.
    { label: "Schools / Branches",   path: "/schools",          superDevOnly: true },
    { label: "All Schools Overview", path: "/schools/overview", chainReadOnly: true },
  ]},
  SITE_CONTENT_GROUP,
  { label: "Announcements", path: "/announcements" },
  // The top-level "Calendar" (school-wide events) entry used to live
  // here. It was retired because the Payroll → HR Calendar already
  // surfaces holidays, training, meetings, and leaves in one place, so
  // a separate top-nav tab was a duplicate. The `/calendar` route
  // itself is kept (in App.jsx) so any existing bookmark still resolves.
  { label: "Reports",       path: "/reports" },
];

const CONTENT_MANAGER_NAV = [
  { label: "Dashboard", path: "/dashboard" },
  SITE_CONTENT_GROUP,
  { label: "Announcements", path: "/announcements" },
];

/** Fallback nav for office / staff roles that don't have a role-specific nav
 *  yet. Articles was removed because website content now belongs to the
 *  ContentManager role, not every random office staffer.
 *
 *  Note: top-level "Calendar" (school events) is intentionally NOT here.
 *  Back-office roles get a personal calendar via My Workspace → My Calendar
 *  which already overlays school holidays / training / meetings, so the
 *  separate school-events tab would just look like a duplicate. */
const STAFF_MINI_NAV = [
  { label: "Dashboard",     path: "/dashboard" },
  { label: "Announcements", path: "/announcements" },
];

/** Nurse / ClinicStaff get the full clinic workspace — patients,
 *  visits, medication, allergies. The /clinic/students route is
 *  role-gated to ["Nurse", "ClinicStaff", "Admin", "SuperAdmin"] on
 *  the backend AND in App.jsx via RequireRole, so non-clinic guesses
 *  at the URL get bounced. */
const CLINIC_NAV = [
  { label: "Dashboard",     path: "/dashboard" },
  { label: "Clinic",        path: "/clinic/students" },
  { label: "Announcements", path: "/announcements" },
];

/** ComputerLab / ComputerLabStaff — the IT teacher's workspace.
 *  Inventory, repairs, procurement, and student requirements live
 *  behind one /itlab route, role-gated to
 *  ["ComputerLab", "ComputerLabStaff", "Admin", "SuperAdmin"] on
 *  both the backend (ItLabAuth) and App.jsx (RequireRole). */
const IT_LAB_NAV = [
  { label: "Dashboard",     path: "/dashboard" },
  { label: "IT Lab",        path: "/itlab" },
  { label: "Announcements", path: "/announcements" },
];

/** Cashier / CashierStaff own tuition billing and payments, so their nav
 *  surfaces the two tools they actually use every day. School-events
 *  Calendar is omitted — see STAFF_MINI_NAV note. */
const CASHIER_NAV = [
  { label: "Dashboard",            path: "/dashboard" },
  { label: "Tuition",              path: "/admin/tuition" },
  { label: "Expenses",             path: "/admin/expenses" },
  { label: "Statement of Account", path: "/tuition/soa" },
  { label: "Announcements",        path: "/announcements" },
];

/** Academic Head owns teacher ↔ subject ↔ section assignments. Their nav
 *  points at the tools they actually use: the full Academics CRUD for sections
 *  and class offerings, and the read-only Academic Directory as an overview.
 *
 *  School-events "Calendar" is intentionally NOT here — Academic Head has
 *  My Workspace → My Calendar for personal HR (leaves / training / etc.)
 *  and the school-wide Events Calendar is reserved for roles that actually
 *  manage multiple employees (Admin / SuperAdmin). */
const ACADEMIC_HEAD_NAV = [
  { label: "Dashboard",          path: "/dashboard" },
  { label: "Academics",          path: "/academics" },
  { label: "Admissions",         path: "/admissions/new" },
  { label: "Academic Directory", path: "/academic-directory" },
  { label: "Announcements",      path: "/announcements" },
  { label: "Class Schedule",     path: "/schedule" },
];

/** Registrar (and Registrar Staff) are the keepers of enrolment and student
 *  records. Their nav points straight at those tools — no content pages.
 *  School-events Calendar is omitted (see ACADEMIC_HEAD_NAV note). */
const REGISTRAR_NAV = [
  { label: "Dashboard",          path: "/dashboard" },
  { label: "Admissions",         path: "/admissions/new" },
  { label: "Students & Parents", path: "/directory" },
  { label: "Academic Directory", path: "/academic-directory" },
  { label: "Announcements",      path: "/announcements" },
];

const NAV_BY_ROLE = {
  // SuperDev (project developer) is the highest-privilege role. It gets
  // the full Admin nav so the dev can reach every page from the topbar
  // without typing URLs manually.
  SuperDev:       ADMIN_NAV,
  SuperAdmin:     ADMIN_NAV,
  Admin:          ADMIN_NAV,
  ContentManager: CONTENT_MANAGER_NAV,
  AcademicHead:   ACADEMIC_HEAD_NAV,
  // Teacher / Student / Parent — the "Calendar" group used to bundle
  // Events Calendar (school-wide) + Class Schedule (personal). Events
  // Calendar is now reserved for Admin/SuperAdmin (multi-employee
  // oversight); Class Schedule stays as a flat link since it's the
  // user's own schedule. Personal HR (leaves) lives under My Workspace
  // → My Calendar for staff roles.
  Teacher: [
    { label: "Dashboard",          path: "/dashboard" },
    { label: "Academic Directory", path: "/academic-directory" },
    // Top-level teacher workspace destinations — the encoder hub and
    // students directory live under MyGradingPage / MyStudentsPage. Both
    // routes are role-gated to ["Teacher", "Admin"] in App.jsx, so a
    // non-teacher who guesses the URL is bounced back by RequireRole.
    { label: "Attendance",         path: "/my-attendance" },
    { label: "Grading",            path: "/my-grading" },
    { label: "Students",           path: "/my-students" },
    { label: "Announcements",      path: "/announcements" },
    { label: "Class Schedule",     path: "/schedule" },
  ],
  Student: [
    { label: "Dashboard",          path: "/dashboard" },
    { label: "Academic Directory", path: "/academic-directory" },
    { label: "Grades",             path: "/grades" },
    { label: "Tuition",            path: "/tuition" },
    { label: "Statement of Account", path: "/tuition/soa" },
    { label: "Books",              path: "/books" },
    { label: "Announcements",      path: "/announcements" },
    { label: "Class Schedule",     path: "/schedule" },
  ],
  Parent: [
    { label: "Dashboard",          path: "/dashboard" },
    { label: "Academic Directory", path: "/academic-directory" },
    { label: "Grades",             path: "/grades" },
    { label: "Tuition",            path: "/tuition" },
    { label: "Statement of Account", path: "/tuition/soa" },
    { label: "Books",              path: "/books" },
    { label: "Announcements",      path: "/announcements" },
    { label: "Class Schedule",     path: "/schedule" },
  ],
  Janitor: [
    { label: "Dashboard",     path: "/dashboard" },
    { label: "Announcements", path: "/announcements" },
  ],
  SecurityGuard: [
    { label: "Dashboard",     path: "/dashboard" },
    { label: "Announcements", path: "/announcements" },
  ],
  Registrar:      REGISTRAR_NAV,
  RegistrarStaff: REGISTRAR_NAV,
  Cashier:        CASHIER_NAV,
  HR:             STAFF_MINI_NAV,
  Librarian:      STAFF_MINI_NAV,
  Nurse:          CLINIC_NAV,
  GuidanceOffice: STAFF_MINI_NAV,
  CashierStaff:   CASHIER_NAV,
  LibraryStaff:   STAFF_MINI_NAV,
  ClinicStaff:    CLINIC_NAV,
  ComputerLab:      IT_LAB_NAV,
  ComputerLabStaff: IT_LAB_NAV,
};

/** Shared Help entry appended to every role's nav. The Help page itself
 *  shows only guides relevant to the logged-in role, so a single link is
 *  enough — no need to duplicate the entry in every nav array. */
const HELP_NAV_ITEM = { label: "Help", path: "/help" };

function navForRole(role) {
  const base = NAV_BY_ROLE[role] || [{ label: "Dashboard", path: "/dashboard" }];
  // Defensive: skip the auto-append if a role-specific nav ever adds its
  // own Help entry (none do today).
  if (base.some(e => e.path === "/help")) return base;
  return [...base, HELP_NAV_ITEM];
}

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

function NavGroup({ entry, pathname, nav }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const groupActive = entry.items.some(it => pathname === it.path);

  return (
    <div className={styles.navGroup} ref={ref}>
      <div
        className={`${styles.navItem} ${groupActive || open ? styles.active : ""}`}
        onClick={() => setOpen(o => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") setOpen(o => !o); }}>
        <span>{entry.label}</span>
        <svg
          className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
          width="10" height="10" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 4.5 L6 8 L9.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      {open && (
        <div className={styles.dropdown}>
          {entry.items.map(it => (
            <div
              key={it.label}
              className={`${styles.dropItem} ${pathname === it.path ? styles.dropItemActive : ""}`}
              onClick={() => { setOpen(false); nav(it.path); }}>
              {it.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Bell-icon dropdown showing pending Leave + Overtime approvals.
 *  Inline-styled so it doesn't need new CSS module classes. Decided
 *  notifications can be dismissed via "Clear all"; pending ones can
 *  only disappear by being decided (the requester / approver must
 *  not lose track of them). */
function ApprovalsBell({
  user, isAdmin, pendingLeaves, pendingOT, notifs, onMarkNotifRead, empLookup,
  readKeys, clearedKeys, markRead, clearAllKeys,
  leaveKey, otKey, bellOpen, setBellOpen, bellRef, BELL_MAX,
  onRefresh, nav,
}) {
  const liveLeave = pendingLeaves.filter(ev => !clearedKeys.has(leaveKey(ev)));
  const liveOT    = pendingOT.filter(ts => !clearedKeys.has(otKey(ts)));
  const liveNotifs = (notifs || []).filter(n => !n.readAt);
  const unreadLeave = liveLeave.filter(ev => !readKeys.has(leaveKey(ev))).length;
  const unreadOT    = liveOT.filter(ts => !readKeys.has(otKey(ts))).length;
  const total = unreadLeave + unreadOT + liveNotifs.length;

  const chip = (s) =>
    s === "APPROVED" ? { bg: "#dcfce7", fg: "#166534", dot: "#15803d" }
    : s === "REJECTED" ? { bg: "#fee2e2", fg: "#991b1b", dot: "#b91c1c" }
    : { bg: "#fef3c7", fg: "#92400e", dot: "#c0392b" };

  return (
    <div ref={bellRef} style={{ position: "relative" }}>
      <button
        type="button"
        className={`${styles.actionBtn} ${total > 0 ? styles.actionBtnActive : ""}`}
        title={total ? `${total} new notification${total === 1 ? "" : "s"}` : "No new notifications"}
        aria-label="Approvals"
        aria-haspopup="menu"
        aria-expanded={bellOpen}
        onClick={() => setBellOpen(o => { if (!o) onRefresh(); return !o; })}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.8"
             strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9z" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {total > 0 && (
          <span aria-label={`${total} unread`} style={{
            position: "absolute", top: 2, right: 2,
            minWidth: 16, height: 16, padding: "0 4px",
            borderRadius: 8, background: "#c0392b", color: "#fff",
            fontSize: 10, fontWeight: 800, lineHeight: "16px",
            textAlign: "center", boxShadow: "0 0 0 2px #fff",
          }}>
            {total > 9 ? "9+" : total}
          </span>
        )}
      </button>

      {bellOpen && (
        <div role="menu" style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0,
          width: 380, maxHeight: 520, overflowY: "auto",
          background: "#fff", border: "1px solid #efe9dc",
          borderRadius: 14, padding: 0, zIndex: 60,
          boxShadow: "0 4px 12px rgba(0,0,0,.06), 0 18px 40px rgba(44,24,16,.14)",
        }}>
          {(() => {
            const decidedKeys = [
              ...liveLeave.filter(ev => ev.status === "APPROVED" || ev.status === "REJECTED").map(leaveKey),
              ...liveOT.filter(ts => ts.overtimeStatus === "APPROVED" || ts.overtimeStatus === "REJECTED").map(otKey),
            ];
            if (decidedKeys.length === 0) return null;
            return (
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 12px 0" }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); clearAllKeys(decidedKeys); }}
                  title="Clear approved / rejected notifications. Pending items stay until decided."
                  style={{
                    background: "none", border: "none", padding: "4px 8px",
                    cursor: "pointer", fontSize: 11, fontWeight: 700,
                    letterSpacing: ".3px", color: "#2563eb",
                  }}>
                  Clear all
                </button>
              </div>
            );
          })()}

          {/* Updates — "your record was changed by …" feed. Sits above
              the approval queues so personal activity lands first;
              approvals (queue work) are secondary. */}
          {liveNotifs.length > 0 && (
            <>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 14px 6px", fontSize: 11, fontWeight: 800,
                color: "#3d342a", letterSpacing: ".5px", textTransform: "uppercase",
              }}>
                <span>Updates</span>
                <span style={{ color: "#92400e", fontWeight: 600 }}>
                  {liveNotifs.length} NEW
                </span>
              </div>
              <div>
                {liveNotifs.slice(0, BELL_MAX).map(n => {
                  // Per-type dot colour so the bell reads as a quick
                  // status feed (green = grade/approval, red = rejection,
                  // gold = generic, blue = payment received).
                  const c = n.type === "LEAVE_APPROVED"
                    ? { dot: "#15803d" }
                    : n.type === "LEAVE_REJECTED"
                    ? { dot: "#b91c1c" }
                    : n.type === "GRADE_UPDATED"
                    ? { dot: "#1f6a2d" }
                    : n.type === "PAYMENT_RECEIVED"
                    ? { dot: "#1d4a8e" }
                    : { dot: "#c9a14a" };
                  // Deep-link destination per entity type. Grade and
                  // payment notifications land on the student-side pages
                  // they already see in the nav.
                  const target = n.entityType === "TIMESHEET"
                    ? "/my-timesheet"
                    : n.entityType === "GRADE"
                    ? "/grades"
                    : n.entityType === "TUITION_PAYMENT"
                    ? "/tuition/soa"
                    : "/my-calendar";
                  return (
                    <div key={`notif-${n.id}`}
                         onClick={() => {
                           onMarkNotifRead && onMarkNotifRead(n);
                           setBellOpen(false);
                           nav(target);
                         }}
                         style={{
                           display: "flex", gap: 10, padding: "10px 14px",
                           cursor: "pointer", fontSize: 13,
                           borderTop: "1px solid #f5f0e4",
                           background: "rgba(201,161,74,.08)",
                         }}>
                      <span style={{
                        width: 8, height: 8, borderRadius: "50%",
                        background: c.dot, marginTop: 6, flexShrink: 0,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: "#1a1a1a" }}>
                          {n.message}
                          <span title="New" style={{
                            marginLeft: 6, display: "inline-block",
                            width: 7, height: 7, borderRadius: "50%",
                            background: "#2563eb", verticalAlign: "middle",
                          }} />
                        </div>
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                          {fmtRelative(n.createdAt)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {liveNotifs.length > BELL_MAX && (
                <div style={{
                  padding: "6px 16px", textAlign: "center",
                  fontSize: 11, color: "#6b7280", background: "#fafafa",
                  borderTop: "1px solid #f1ece1",
                }}>
                  +{liveNotifs.length - BELL_MAX} more
                </div>
              )}
            </>
          )}

          {/* Leave Requests */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "10px 14px 6px", fontSize: 11, fontWeight: 800,
            color: "#3d342a", letterSpacing: ".5px", textTransform: "uppercase",
            borderTop: liveNotifs.length > 0 ? "1px solid #f1ece1" : "none",
          }}>
            <span>Leave Requests</span>
            {liveLeave.length > 0 && (
              <span style={{ color: "#92400e", fontWeight: 600 }}>{liveLeave.length} ITEM{liveLeave.length === 1 ? "" : "S"}</span>
            )}
          </div>
          <div>
            {liveLeave.length === 0 && liveOT.length === 0 && liveNotifs.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>✓</div>
                You're all caught up.
              </div>
            ) : liveLeave.length === 0 ? null : (
              [...liveLeave].sort((a, b) => {
                const aPend = (a.status === "PENDING" || a.status == null) ? 0 : 1;
                const bPend = (b.status === "PENDING" || b.status == null) ? 0 : 1;
                if (aPend !== bPend) return aPend - bPend;
                const at = a.decidedAt ? new Date(a.decidedAt).getTime() : 0;
                const bt = b.decidedAt ? new Date(b.decidedAt).getTime() : 0;
                return bt - at;
              }).slice(0, BELL_MAX).map(ev => {
                const range = ev.endDate && ev.endDate !== ev.startDate
                  ? `${ev.startDate} → ${ev.endDate}` : ev.startDate;
                const isMine = ev.createdBy && ev.createdBy === user?.employeeCode;
                const who = isMine ? "Your request" : `by ${empLookup[ev.createdBy] || ev.createdBy || "Admin"}`;
                const s = ev.status || "PENDING";
                const c = chip(s);
                const k = leaveKey(ev);
                const unread = !readKeys.has(k);
                return (
                  <div key={ev.id}
                       onClick={() => { markRead(k); setBellOpen(false); nav("/payroll-calendar"); }}
                       style={{
                         display: "flex", gap: 10, padding: "10px 14px",
                         cursor: "pointer", fontSize: 13,
                         borderTop: "1px solid #f5f0e4",
                         background: unread ? "rgba(37,99,235,.06)" : "transparent",
                       }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.dot, marginTop: 6, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: "#1a1a1a" }}>
                        {ev.title}
                        <span style={{
                          marginLeft: 8, fontSize: 9, fontWeight: 700,
                          padding: "1px 6px", borderRadius: 999, letterSpacing: ".4px",
                          background: c.bg, color: c.fg, verticalAlign: "middle",
                        }}>{s}</span>
                        {unread && (
                          <span title="New" style={{
                            marginLeft: 6, display: "inline-block",
                            width: 7, height: 7, borderRadius: "50%",
                            background: "#2563eb", verticalAlign: "middle",
                          }} />
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                        {who} · {range}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {liveLeave.length > BELL_MAX && (
            <div style={{
              padding: "6px 16px", textAlign: "center",
              fontSize: 11, color: "#6b7280", background: "#fafafa",
              borderTop: "1px solid #f1ece1",
            }}>
              +{liveLeave.length - BELL_MAX} more — see Calendar
            </div>
          )}
          {liveLeave.length > 0 && (
            <div onClick={() => { setBellOpen(false); nav("/payroll-calendar"); }}
                 style={{
                   padding: "10px 14px", textAlign: "center",
                   fontSize: 12, fontWeight: 700, color: "#c9a227",
                   borderTop: "1px solid #f1ece1", cursor: "pointer",
                 }}>
              Open Calendar →
            </div>
          )}

          {/* Overtime Requests */}
          {liveOT.length > 0 && (
            <>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 14px 6px", fontSize: 11, fontWeight: 800,
                color: "#3d342a", letterSpacing: ".5px", textTransform: "uppercase",
                borderTop: "1px solid #f1ece1",
              }}>
                <span>Overtime Requests</span>
                {liveOT.filter(t => t.overtimeStatus === "PENDING").length > 0 && (
                  <span style={{ color: "#92400e", fontWeight: 600 }}>
                    {liveOT.filter(t => t.overtimeStatus === "PENDING").length} PENDING
                  </span>
                )}
              </div>
              <div>
                {liveOT.slice(0, BELL_MAX).map(ts => {
                  const isMine = ts.employeeCode === user?.employeeCode;
                  const who = isMine ? "Your request" : `by ${empLookup[ts.employeeCode] || ts.employeeCode || "Employee"}`;
                  const s = ts.overtimeStatus || "PENDING";
                  const c = chip(s);
                  const k = otKey(ts);
                  const unread = !readKeys.has(k);
                  const dateStr = Array.isArray(ts.date)
                    ? `${ts.date[0]}-${String(ts.date[1]).padStart(2, "0")}-${String(ts.date[2]).padStart(2, "0")}`
                    : ts.date;
                  return (
                    <div key={`ot-${ts.id}`}
                         onClick={() => { markRead(k); setBellOpen(false); nav("/timesheet"); }}
                         style={{
                           display: "flex", gap: 10, padding: "10px 14px",
                           cursor: "pointer", fontSize: 13,
                           borderTop: "1px solid #f5f0e4",
                           background: unread ? "rgba(37,99,235,.06)" : "transparent",
                         }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.dot, marginTop: 6, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: "#1a1a1a" }}>
                          Overtime · {ts.overtimeHours || 0} hr{ts.overtimeHours === 1 ? "" : "s"}
                          <span style={{
                            marginLeft: 8, fontSize: 9, fontWeight: 700,
                            padding: "1px 6px", borderRadius: 999, letterSpacing: ".4px",
                            background: c.bg, color: c.fg, verticalAlign: "middle",
                          }}>{s}</span>
                          {unread && (
                            <span title="New" style={{
                              marginLeft: 6, display: "inline-block",
                              width: 7, height: 7, borderRadius: "50%",
                              background: "#2563eb", verticalAlign: "middle",
                            }} />
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                          {who} · {dateStr}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {liveOT.length > BELL_MAX && (
                <div style={{
                  padding: "6px 16px", textAlign: "center",
                  fontSize: 11, color: "#6b7280", background: "#fafafa",
                  borderTop: "1px solid #f1ece1",
                }}>
                  +{liveOT.length - BELL_MAX} more — see Timesheet
                </div>
              )}
              <div onClick={() => { setBellOpen(false); nav("/timesheet"); }}
                   style={{
                     padding: "10px 14px", textAlign: "center",
                     fontSize: 12, fontWeight: 700, color: "#c9a227",
                     borderTop: "1px solid #f1ece1", cursor: "pointer",
                   }}>
                Open Timesheet →
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function Topbar() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const user = getUser();
  const payrollOn = usePayrollEnabled();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const userMenuRef  = useRef(null);
  const approvalsRef = useRef(null);

  // Inbox state — merges PaymentDeletionRequest and PaymentEditRequest into
  // a single chronological list. Each item carries a `_kind` discriminator:
  //   "delete"  → cashier requesting that a payment be removed
  //   "edit"    → cashier requesting that a payment's fields be changed
  //
  // What the user sees depends on their role:
  //   - Admin / SuperAdmin     → all PENDING delete requests they can act on
  //   - Owner  / SuperAdmin    → all PENDING edit   requests they can act on
  //   - Cashier / CashierStaff → their own requests of either kind across
  //                              all statuses, so they can see the verdict.
  // SuperAdmin sits at both gates, so they see the union of all PENDING.
  const [inboxItems, setInboxItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [approvalErr, setApprovalErr] = useState("");
  // Request whose full-details popup is open (deletion-request only — the
  // existing ApprovalDetailsModal is wired for that shape; edit requests
  // get an inline diff card instead).
  const [detailsRequest, setDetailsRequest] = useState(null);
  // Reject dialog target — { item, busy } | null. Populated on click of the
  // Reject button anywhere in the inbox; cleared on confirm/cancel.
  const [rejectTarget, setRejectTarget] = useState(null);

  // SuperDev (project developer, hidden role) inherits every chrome
  // privilege a SuperAdmin gets — Settings menu item, approvals bell,
  // and any other isAdmin-gated UI in this file. The role itself is
  // never rendered or named in the chrome; we just treat the dev as
  // a virtual admin for visibility purposes.
  const isAdmin           = user?.role === "SuperAdmin"
                         || user?.role === "Admin"
                         || user?.role === "SuperDev";
  const isOwner           = user?.role === "Owner";
  const isSuperAdmin      = user?.role === "SuperAdmin";
  const isCashier         = user?.role === "Cashier"    || user?.role === "CashierStaff";
  // Approval gates — match the backend exactly:
  //   delete: requireStrictAdmin (Admin / SuperAdmin)
  //   edit:   requireOwnerOrSuperAdmin (Owner / SuperAdmin) — Admin not enough
  const canApproveDeletes = isAdmin;
  const canApproveEdits   = isOwner || isSuperAdmin;
  const showInbox         = canApproveDeletes || canApproveEdits || isCashier;

  // ── Approvals bell (Leave + Overtime) ─────────────────────────────
  // Lives next to the payment-deletion inbox, not merged with it. The
  // inbox is school-system-specific (payment delete approvals); the bell
  // surfaces the InventorySales-style HR approvals (Leave, OT). Hidden
  // entirely when the Payroll module is off — leave / timesheet / OT
  // all live under that module, so a non-payroll deployment shouldn't
  // see an empty bell.
  const BELL_MAX = 5;
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef(null);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [pendingOT, setPendingOT] = useState([]);
  // Personal "your record was changed by …" feed (from /api/notifications).
  // Distinct from pendingLeaves/pendingOT because those are the approval
  // queue; this one is "things that happened to your data".
  const [notifs, setNotifs] = useState([]);
  const [empLookup, setEmpLookup] = useState({});
  // Resolve the "notification code" each role uses as their
  // recipient identity on the /api/notifications scope query.
  //   Staff   → employeeCode (existing convention)
  //   Student → linkedEntity.studentNumber (matches what the backend
  //             writes on grade / payment notifications about them)
  //   Parent  → AppUser.username (the backend's parent fan-out writes
  //             notifications with username as recipient_code so the
  //             parent sees their child's events here)
  // The header is set on every bell fetch as X-Employee-Code, and the
  // backend filters notifications.recipient_code = X-Employee-Code,
  // so as long as both sides agree on the per-role code, it works
  // end-to-end with zero schema change.
  const notifCode =
    user?.employeeCode
    || (user?.role === "Student" ? user?.linkedEntity?.studentNumber : "")
    || (user?.role === "Parent"  ? user?.username                    : "")
    || "";

  // Students and Parents get the bell purely for personal notifications
  // (grades posted, payment recorded). They never see the payroll-only
  // leave / OT tabs because those feeds return empty for non-staff —
  // the bell just degrades to a personal-notification list for them.
  const isStudentOrParent = user?.role === "Student" || user?.role === "Parent";
  const canSeeBell = (
    (payrollOn && (!!user?.employeeCode || isAdmin || user?.role === "Owner"))
    || (isStudentOrParent && !!notifCode)
  );
  const canSeeOTBell = canSeeBell;

  // Per-user read / cleared persistence. Keys are `<type>-<id>-<status>`
  // so a status flip remints the identity and the bell ticks back up.
  const readStorageKey    = `bell.read.${user?.username || "guest"}`;
  const clearedStorageKey = `bell.cleared.${user?.username || "guest"}`;
  const [readKeys, setReadKeys] = useState(() => {
    try {
      const raw = localStorage.getItem(`bell.read.${user?.username || "guest"}`);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const [clearedKeys, setClearedKeys] = useState(() => {
    try {
      const raw = localStorage.getItem(`bell.cleared.${user?.username || "guest"}`);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  function persistSet(key, set) {
    try { localStorage.setItem(key, JSON.stringify([...set])); }
    catch { /* storage full / disabled — non-fatal */ }
  }
  const leaveKey = (ev) => `leave-${ev.id}-${ev.status || "PENDING"}`;
  const otKey    = (ts) => `ot-${ts.id}-${ts.overtimeStatus || "PENDING"}`;
  function markRead(key) {
    setReadKeys(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      persistSet(readStorageKey, next);
      return next;
    });
  }
  function clearAllKeys(keys) {
    if (!keys.length) return;
    setClearedKeys(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const k of keys) if (!next.has(k)) { next.add(k); changed = true; }
      if (changed) persistSet(clearedStorageKey, next);
      return changed ? next : prev;
    });
    setReadKeys(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const k of keys) if (!next.has(k)) { next.add(k); changed = true; }
      if (changed) persistSet(readStorageKey, next);
      return changed ? next : prev;
    });
  }

  // Manual-refresh tick. Bumped on bell open + on `approvals:changed`
  // events from CalendarPage / TimesheetPage so an action lands without
  // waiting on the 60s poll.
  const [approvalsTick, setApprovalsTick] = useState(0);

  // Headers a non-admin needs so the backend's manager / requester
  // visibility rules pick the right rows. Same shape as TimesheetPage.
  const bellHeaders = () => ({
    "X-User-Role":     user?.role || "",
    // Students/Parents lack an employeeCode — fall back to their student
    // number so the personal notification fetch returns rows for them.
    "X-Employee-Code": notifCode,
    "X-Username":      user?.username || "system",
  });

  // Badge math:
  //   - Approver: every pending item is something they need to action.
  //   - Cashier: things they should look at = still PENDING (waiting on admin)
  //     plus any REJECTED that they need to read the reason for. Approved
  //     ones don't need a badge — the change just lands silently, that's the
  //     signal it succeeded.
  const isApprover = canApproveDeletes || canApproveEdits;
  const inboxBadge = isApprover
    ? inboxItems.length
    : inboxItems.filter(r => r.status === "PENDING" || r.status === "REJECTED").length;

  async function loadInbox() {
    if (!showInbox) return;
    // Build the URLs we actually need to hit. Admin-only roles only fetch
    // delete; Owner-only roles only fetch edit; SuperAdmin fetches both.
    // Cashiers always fetch both — their own requests of each kind.
    const fetches = [];
    if (isCashier) {
      const me = encodeURIComponent(user?.username || "");
      fetches.push({ kind: "delete", url: `${API_BASE}/api/payment-deletion-requests?requestedBy=${me}` });
      fetches.push({ kind: "edit",   url: `${API_BASE}/api/payment-edit-requests?requestedBy=${me}` });
    } else {
      if (canApproveDeletes) fetches.push({ kind: "delete", url: `${API_BASE}/api/payment-deletion-requests?status=PENDING` });
      if (canApproveEdits)   fetches.push({ kind: "edit",   url: `${API_BASE}/api/payment-edit-requests?status=PENDING` });
    }
    if (fetches.length === 0) { setInboxItems([]); return; }

    try {
      const results = await Promise.all(fetches.map(f =>
        fetch(f.url).then(r => r.ok ? r.json() : []).catch(() => [])
      ));
      const merged = [];
      results.forEach((rows, i) => {
        const kind = fetches[i].kind;
        (Array.isArray(rows) ? rows : []).forEach(r => {
          merged.push({ ...r, _kind: kind });
        });
      });
      // Newest first across both kinds — same chronological feel admins
      // expect when scanning a single queue.
      merged.sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
      setInboxItems(merged);
    } catch { /* offline / network — leave the list as-is */ }
  }

  useEffect(() => {
    if (!showInbox) return undefined;
    loadInbox();
    const id = setInterval(loadInbox, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInbox]);

  // Pending leave approvals for the bell. Keeps PENDING items + the
  // caller's own recently-decided ones (≤7 days) so a requester sees
  // "your leave was approved" without opening the calendar.
  useEffect(() => {
    // Students/parents never have leave records — skip the fetch for
    // them even though they can see the bell (it would just 200 with
    // an empty list and waste a request every 60s).
    if (!canSeeBell || isStudentOrParent) {
      setPendingLeaves([]);
      return undefined;
    }
    let cancelled = false;
    async function fetchLeaves() {
      try {
        const res = await fetch(`${API_BASE}/api/calendar/events/approvals`, {
          headers: bellHeaders(),
        });
        if (!res.ok) return;
        const rows = await res.json();
        const all = Array.isArray(rows) ? rows : [];
        const RECENT_MS = 7 * 24 * 60 * 60 * 1000;
        const now = Date.now();
        const filtered = all.filter(ev => {
          const s = ev.status;
          const mine = ev.createdBy && ev.createdBy === user?.employeeCode;
          if (s === "PENDING" || s == null) return true;
          if ((s === "APPROVED" || s === "REJECTED") && mine && ev.decidedAt) {
            const t = new Date(ev.decidedAt).getTime();
            if (!isNaN(t) && now - t <= RECENT_MS) return true;
          }
          return false;
        });
        if (!cancelled) setPendingLeaves(filtered);
      } catch { /* offline / transient */ }
    }
    fetchLeaves();
    const id = setInterval(fetchLeaves, 60_000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeBell, user?.employeeCode, approvalsTick]);

  // Pending overtime approvals — same cadence. Gated on the Payroll
  // module: when payroll is off, no timesheets / OT exist, so we skip
  // the poll entirely and clear any leftover state from a previous
  // payroll-enabled session.
  useEffect(() => {
    // Same student/parent skip as the leave fetcher — they have no
    // OT records, so leave the array empty and don't poll.
    if (!canSeeOTBell || isStudentOrParent) {
      setPendingOT([]);
      return undefined;
    }
    let cancelled = false;
    async function fetchOt() {
      try {
        const res = await fetch(`${API_BASE}/api/timesheets/overtime-approvals`, {
          headers: bellHeaders(),
        });
        if (!res.ok) return;
        const rows = await res.json();
        if (!cancelled) setPendingOT(Array.isArray(rows) ? rows : []);
      } catch { /* offline / transient */ }
    }
    fetchOt();
    const id = setInterval(fetchOt, 60_000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeOTBell, user?.role, user?.employeeCode, approvalsTick]);

  // Personal notifications feed — anyone with an employee code gets it
  // (admins included; an admin can have their own timesheet edited by
  // another admin and care about that just like everyone else).
  useEffect(() => {
    // Gate the fetch on the resolved notification code (staff
    // employeeCode OR student/parent fallback). When the code is empty
    // we skip entirely — the backend's /api/notifications requires
    // X-Employee-Code and would 401 otherwise.
    if (!notifCode) {
      setNotifs([]);
      return undefined;
    }
    let cancelled = false;
    async function fetchNotifs() {
      try {
        const res = await fetch(`${API_BASE}/api/notifications?limit=20`, {
          headers: bellHeaders(),
        });
        if (!res.ok) return;
        const rows = await res.json();
        if (!cancelled) setNotifs(Array.isArray(rows) ? rows : []);
      } catch { /* offline / transient */ }
    }
    fetchNotifs();
    const id = setInterval(fetchNotifs, 60_000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifCode, approvalsTick]);

  // Listen for cross-page "approvals:changed" events. CalendarPage
  // dispatches after a Leave save / decide, TimesheetPage after an OT
  // request / cancel / decide. Bumps the tick so both fetchers re-run.
  useEffect(() => {
    if (!canSeeBell) return undefined;
    const onChange = () => setApprovalsTick(t => t + 1);
    window.addEventListener("approvals:changed", onChange);
    return () => window.removeEventListener("approvals:changed", onChange);
  }, [canSeeBell]);

  // One-time employee lookup so notifications show "by Maria Santos"
  // instead of a raw code.
  useEffect(() => {
    if (!canSeeBell) return undefined;
    let cancelled = false;
    fetch(`${API_BASE}/api/calendar/employees`)
      .then(r => r.ok ? r.json() : [])
      .then(rows => {
        if (cancelled) return;
        const map = {};
        for (const e of (Array.isArray(rows) ? rows : [])) map[e.code] = e.name || e.code;
        setEmpLookup(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canSeeBell]);

  /** Resolve the right backend endpoint for a request — different table
   *  per kind, but identical approve / reject verbs. */
  function endpointFor(reqRow) {
    return reqRow._kind === "edit"
      ? `${API_BASE}/api/payment-edit-requests`
      : `${API_BASE}/api/payment-deletion-requests`;
  }

  async function approveRequest(reqRow) {
    setBusyId(reqRow.id); setApprovalErr("");
    try {
      const res = await fetch(`${endpointFor(reqRow)}/${reqRow.id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Approve failed (${res.status})`));
      await loadInbox();
    } catch (e) { setApprovalErr(e.message); }
    finally { setBusyId(null); }
  }

  /** Open the styled RejectReasonDialog — replaces the old window.prompt
   *  so the approver gets a proper textarea, focus management, and a
   *  Cmd/Ctrl+Enter shortcut. The actual POST happens in confirmReject. */
  function rejectRequest(reqRow) {
    setApprovalErr("");
    setRejectTarget(reqRow);
  }

  async function confirmReject(reason) {
    if (!rejectTarget || !reason?.trim()) return;
    const reqRow = rejectTarget;
    setBusyId(reqRow.id); setApprovalErr("");
    try {
      const res = await fetch(`${endpointFor(reqRow)}/${reqRow.id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Reject failed (${res.status})`));
      setRejectTarget(null);
      await loadInbox();
    } catch (e) { setApprovalErr(e.message); }
    finally { setBusyId(null); }
  }

  useEffect(() => {
    function onClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (approvalsRef.current && !approvalsRef.current.contains(e.target)) {
        setApprovalsOpen(false);
      }
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setBellOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem("user");
    localStorage.removeItem("user");
    nav("/login", { replace: true });
  };

  return (
    <div className={styles.topbar}>

      {/* ── Brand ── */}
      <div
        className={styles.brand}
        onClick={() => nav("/")}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") nav("/"); }}
        role="button"
        tabIndex={0}
        title="Go to home"
        style={{ cursor: "pointer" }}
      >
        <img
          src="/ncla-logo.png"
          alt="NCLA logo"
          className={styles.brandLogoMark}
        />
        <div className={styles.brandText}>
          <div className={styles.brandTitle}>NORTHPOINT CENTER</div>
          <div className={styles.brandSubtitle}>for Learning and Advancement (NCLA)</div>
        </div>
      </div>

      {/* ── School pill (multi-tenant) ──
          Branch users see their school's name + logo. Chain admins
          (SuperAdmin / SuperDev) get a dropdown to switch context.
          The pill hides entirely if no school is set on the session
          (legacy logins, /login screen). */}
      <SchoolPill user={user} />

      {/* ── Nav tabs ── */}
      <div className={styles.navPill}>
        {(() => {
          // Inject the optional Payroll module group for SuperAdmin /
          // SuperDev when the Settings → Maintenance toggle is on. Help
          // is the last entry in the role nav, so we slot Payroll just
          // before it to keep Help anchored on the right.
          let base = navForRole(user?.role);

          // Two tier-based visibility tags applied here:
          //   - `superDevOnly` → SuperDev only.
          //   - `chainReadOnly` → SuperDev / SuperAdmin / Owner
          //     (the chain-READ tier that can switch branches and
          //     view the cross-school overview).
          // Admin and lower see neither. The filter runs both at top
          // level entries AND inside group `items`. An empty group
          // (every item filtered out) is removed entirely so the
          // user doesn't see a dropdown trigger that opens to
          // nothing.
          const role = user?.role;
          const isSuperDevCaller = role === "SuperDev";
          const isChainReader    = isSuperDevCaller
                                || role === "SuperAdmin"
                                || role === "Owner";
          const keepEntry = (e) => {
            if (e.superDevOnly  && !isSuperDevCaller) return false;
            if (e.chainReadOnly && !isChainReader)    return false;
            return true;
          };
          base = base
            .map(entry => {
              if (entry.items) {
                const filteredItems = entry.items.filter(keepEntry);
                if (filteredItems.length === 0) return null;
                return { ...entry, items: filteredItems };
              }
              return keepEntry(entry) ? entry : null;
            })
            .filter(Boolean);

          const isAdminLikeRole = user?.role === "SuperAdmin"
                               || user?.role === "SuperDev";
          if (isAdminLikeRole && payrollOn) {
            const helpIdx = base.findIndex(e => e.path === "/help");
            base = helpIdx === -1
              ? [...base, PAYROLL_NAV_GROUP]
              : [...base.slice(0, helpIdx), PAYROLL_NAV_GROUP, ...base.slice(helpIdx)];
          }
          // "My Workspace" — personal HR dropdown (Payroll · Timesheet ·
          // Calendar) for every staff account. Don't gate on
          // user.employeeCode here: some roles (Cashier, ContentManager,
          // etc.) link to their employee record via linkedEntity rather
          // than the legacy employeeCode field. The pages themselves render
          // a graceful empty-state for accounts with no employee on file.
          // Gating:
          //   - payrollOn — when the SuperAdmin disables the Payroll
          //     module from Settings, NO staff role should see this
          //     dropdown either; every link inside it points at a
          //     payroll surface that's been switched off. Without this
          //     guard the SuperAdmin's "Payroll" tab hides correctly
          //     but a "My Workspace" tab pops up in its place because
          //     `alreadyHasFullPayroll` flips false too — same shape,
          //     different label, same broken state.
          //   - Skip Students / Parents (they aren't paid / don't clock in).
          //   - Skip SuperAdmins when they already have the full admin
          //     Payroll group so we don't duplicate entries.
          const PAYROLL_HIDDEN_ROLES = new Set(["Student", "Parent"]);
          // SuperDev inherits the SuperAdmin nav, which already includes
          // the full Payroll group above. Without this it'd get the
          // staff "My Workspace" group added on top — visible duplicate.
          const alreadyHasFullPayroll = (user?.role === "SuperAdmin"
                                      || user?.role === "SuperDev") && payrollOn;
          if (payrollOn
              && user?.role
              && !PAYROLL_HIDDEN_ROLES.has(user.role)
              && !alreadyHasFullPayroll) {
            const helpIdx = base.findIndex(e => e.path === "/help");
            // Calendar is renamed to "My Calendar" so it doesn't clash
            // with the top-level "Calendar" tab (school events). Matches
            // the page's own eyebrow header (MyCalendarPage renders
            // "My Calendar / Leaves & HR events"), so the dropdown
            // label, the active URL, and the page title all line up.
            // Payroll/Timesheet stay short — they don't collide with
            // anything else in the nav, so adding "My" just for symmetry
            // would echo the parent "My Workspace" label without
            // resolving any ambiguity.
            const myWorkspace = { label: "My Workspace", items: [
              { label: "Payroll",     path: "/my-payroll"   },
              { label: "Timesheet",   path: "/my-timesheet" },
              { label: "My Calendar", path: "/my-calendar"  },
            ]};
            base = helpIdx === -1
              ? [...base, myWorkspace]
              : [...base.slice(0, helpIdx), myWorkspace, ...base.slice(helpIdx)];
          }
          return base;
        })().map((entry) => {
          if (entry.items) {
            return <NavGroup key={entry.label} entry={entry} pathname={pathname} nav={nav} />;
          }
          const active = pathname === entry.path;
          return (
            <div key={entry.path}
              className={`${styles.navItem} ${active ? styles.active : ""}`}
              onClick={() => nav(entry.path)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") nav(entry.path); }}
              role="button" tabIndex={0}>
              {entry.label}
            </div>
          );
        })}
      </div>

      {/* ── Actions ── */}
      <div className={styles.actions}>
        {/* Approvals bell (Leave + Overtime) — only when the Payroll
            module is enabled and the user has a stake in either side
            (employee can see their own; admin / manager can see what
            they need to decide). */}
        {canSeeBell && (
          <ApprovalsBell
            user={user}
            isAdmin={isAdmin}
            pendingLeaves={pendingLeaves}
            pendingOT={pendingOT}
            notifs={notifs}
            onMarkNotifRead={async (n) => {
              // Optimistic + persist. The next bell-open / poll will
              // reconcile if the server rejects.
              setNotifs(prev => prev.map(x =>
                x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x));
              try {
                await fetch(`${API_BASE}/api/notifications/${n.id}/read`,
                  { method: "POST", headers: bellHeaders() });
              } catch { /* best-effort */ }
            }}
            empLookup={empLookup}
            readKeys={readKeys}
            clearedKeys={clearedKeys}
            markRead={markRead}
            clearAllKeys={clearAllKeys}
            leaveKey={leaveKey}
            otKey={otKey}
            bellOpen={bellOpen}
            setBellOpen={setBellOpen}
            bellRef={bellRef}
            BELL_MAX={BELL_MAX}
            onRefresh={() => setApprovalsTick(t => t + 1)}
            nav={nav}
          />
        )}
        {/* Inbox — admin sees the queue of PENDING requests to action;
            cashier sees their own requests with status pills so they can
            see the admin's verdict (and the reason on rejections). */}
        {showInbox && (
          <div className={styles.userMenuWrap} ref={approvalsRef}
               style={{ position: "relative" }}>
            <button
              type="button"
              className={`${styles.actionBtn} ${approvalsOpen ? styles.actionBtnActive : ""}`}
              onClick={() => setApprovalsOpen(o => !o)}
              title={isApprover ? "Approvals" : "My requests"}
              aria-label={isApprover ? "Approvals" : "My requests"}
              aria-haspopup="menu"
              aria-expanded={approvalsOpen}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7l9 6 9-6" />
                <rect x="3" y="5" width="18" height="14" rx="2" />
              </svg>
              {inboxBadge > 0 && (
                <span aria-label={`${inboxBadge} item${inboxBadge === 1 ? "" : "s"}`}
                  style={{
                    position: "absolute", top: 2, right: 2,
                    minWidth: 16, height: 16, padding: "0 4px",
                    borderRadius: 8, background: "#c0392b", color: "#fff",
                    fontSize: 10, fontWeight: 800, lineHeight: "16px",
                    textAlign: "center", boxShadow: "0 0 0 2px #fff",
                  }}>
                  {inboxBadge > 9 ? "9+" : inboxBadge}
                </span>
              )}
            </button>
            {approvalsOpen && (
              <div className={styles.userMenu} role="menu"
                style={{ width: 360, padding: 12, maxHeight: 480, overflowY: "auto" }}>
                <div style={{ fontWeight: 700, fontSize: "0.85rem",
                              color: "#3d342a", marginBottom: 6,
                              display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>{isApprover ? "Pending Approvals" : "My Requests"}</span>
                  <span style={{ fontSize: "0.72rem", color: "#8a8070", fontWeight: 500 }}>
                    {inboxItems.length === 0 ? "" : `${inboxItems.length} item${inboxItems.length === 1 ? "" : "s"}`}
                  </span>
                </div>
                {approvalErr && (
                  <div style={{ fontSize: "0.78rem", color: "#c0392b",
                                background: "#fdecea", padding: "6px 8px",
                                borderRadius: 6, marginBottom: 8 }}>
                    {approvalErr}
                  </div>
                )}
                {inboxItems.length === 0 ? (
                  <div style={{ fontSize: "0.8rem", color: "#8a8070",
                                padding: "16px 4px", textAlign: "center",
                                fontStyle: "italic" }}>
                    {isApprover ? "No pending approvals." : "You haven't submitted any requests."}
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 8 }}>
                    {inboxItems.map(r => {
                      const isEdit = r._kind === "edit";
                      // Per-item approval gate. Mirrors the backend exactly:
                      //   - delete request → Admin / SuperAdmin
                      //   - edit   request → Owner / SuperAdmin (NOT Admin)
                      // The card hides the action buttons when the viewer
                      // can't act on this kind, even if they're an approver
                      // for the *other* kind.
                      const canActOnThis =
                          (isEdit && canApproveEdits)
                       || (!isEdit && canApproveDeletes);
                      const p = r.payment || {};
                      const stu = r.student || {};
                      const isBusy = busyId === r.id;
                      const studentName = (stu.lastName || stu.firstName)
                        ? `${stu.lastName || ""}, ${stu.firstName || ""}`.replace(/^, |, $/g, "")
                        : "—";
                      // Status pill colours — green/red/yellow keep parity
                      // with the rest of the app's status badges.
                      const statusStyle = r.status === "APPROVED"
                        ? { bg: "#e3f2e6", fg: "#1f6a2d", border: "#bcdcc4" }
                        : r.status === "REJECTED"
                        ? { bg: "#fdecea", fg: "#c0392b", border: "#e6b8b4" }
                        : { bg: "#fff8e1", fg: "#8a6a1c", border: "#efe1a8" };
                      return (
                        <div key={`${r._kind}-${r.id}`} style={{
                          background: "#faf6ee", borderRadius: 8,
                          padding: "10px 12px", border: "1px solid #efe7d6",
                          // Subtle blue left rail for edit requests so the
                          // approver can scan the queue and tell delete from
                          // edit at a glance. Delete keeps the existing
                          // neutral look.
                          borderLeft: isEdit
                            ? "3px solid #2c66c2"
                            : "1px solid #efe7d6",
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between",
                                        alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                            <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
                              {/* Kind tag — distinguishes Edit ✎ from Delete 🗑 */}
                              <span style={{
                                fontSize: "0.62rem", fontWeight: 800,
                                letterSpacing: 0.5, textTransform: "uppercase",
                                padding: "1px 7px", borderRadius: 999,
                                background: isEdit ? "#e2ecf9" : "#fbeae6",
                                color:      isEdit ? "#1d4fa8" : "#a04030",
                                border:     isEdit ? "1px solid #c2d4ee" : "1px solid #e9c0b8",
                                whiteSpace: "nowrap",
                              }}>
                                {isEdit ? "Edit" : "Delete"}
                              </span>
                              <span style={{ fontWeight: 800, color: "#1f6a2d" }}>
                                ₱{fmtMoney(p.amount)}
                              </span>
                              {/* Cashier always wants the status badge front-and-centre.
                                  Approver's queue is implicitly all-PENDING so we hide it
                                  there to keep the card tight. */}
                              {!isApprover && (
                                <span style={{
                                  fontSize: "0.66rem", fontWeight: 800,
                                  letterSpacing: 0.4, textTransform: "uppercase",
                                  padding: "1px 6px", borderRadius: 4,
                                  background: statusStyle.bg, color: statusStyle.fg,
                                  border: `1px solid ${statusStyle.border}`,
                                }}>
                                  {r.status}
                                </span>
                              )}
                            </span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              {/* Full-details popup only handles the delete
                                  request shape today; edit requests show
                                  their diff inline so they don't need it. */}
                              {!isEdit && (
                                <button
                                  type="button"
                                  onClick={() => setDetailsRequest(r)}
                                  title="View full details"
                                  aria-label="View full details"
                                  style={{
                                    width: 22, height: 22, padding: 0,
                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    background: "#fff", color: "#6b5a3a",
                                    border: "1px solid #d6cdb8", borderRadius: "50%",
                                    cursor: "pointer", lineHeight: 1,
                                  }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                       stroke="currentColor" strokeWidth="2.2"
                                       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <circle cx="12" cy="12" r="9" />
                                    <line x1="12" y1="11" x2="12" y2="16" />
                                    <circle cx="12" cy="8" r="0.7" fill="currentColor" stroke="none" />
                                  </svg>
                                </button>
                              )}
                              <span style={{ fontSize: "0.72rem", color: "#8a8070" }}>
                                {fmtRelative(r.createdAt)}
                              </span>
                            </span>
                          </div>
                          <div style={{ fontSize: "0.78rem", color: "#3d342a", marginBottom: 4 }}>
                            <span style={{
                              display: "inline-block", padding: "1px 6px",
                              fontSize: "0.66rem", fontWeight: 800, letterSpacing: 0.4,
                              textTransform: "uppercase",
                              background: "#e8dfc9", color: "#5a4e3a",
                              borderRadius: 4, marginRight: 6,
                            }}>
                              {p.method || "—"}
                            </span>
                            {studentName}
                            {stu.studentNumber ? <span style={{ color: "#8a8070" }}> · #{stu.studentNumber}</span> : null}
                          </div>
                          {/* Approver sees who requested; cashier already
                              knows it was them, so swap to the approver's
                              verdict line. */}
                          {isApprover ? (
                            <div style={{ fontSize: "0.74rem", color: "#6b5a3a", marginBottom: 6 }}>
                              Requested by <b>{r.requestedByUsername || "—"}</b>
                              {r.requestedByRole ? ` · ${r.requestedByRole}` : ""}
                            </div>
                          ) : (
                            r.status !== "PENDING" && (
                              <div style={{ fontSize: "0.74rem", color: "#6b5a3a", marginBottom: 6 }}>
                                {r.status === "APPROVED" ? "Approved" : "Rejected"} by{" "}
                                <b>{r.reviewedByUsername || "admin"}</b>
                                {r.reviewedAt ? ` · ${fmtRelative(r.reviewedAt)}` : ""}
                              </div>
                            )
                          )}

                          {/* Edit-request diff — show every field whose
                              proposed value differs from the snapshot. The
                              backend hands us both maps (proposed/snapshot)
                              so the approver can review the change without
                              opening another popup. */}
                          {isEdit && (() => {
                            const before = r.snapshot || {};
                            const after  = r.proposed || {};
                            const fields = [
                              ["amount",         "Amount",     v => v == null ? "—" : `₱${fmtMoney(v)}`],
                              ["method",         "Method",     v => v || "—"],
                              ["referenceNo",    "Reference",  v => v || "—"],
                              ["bankName",       "Bank",       v => v || "—"],
                              ["appliesToMonth", "Applies to", v => v == null ? "Auto" : (v === 0 ? "Enrollment" : `Month #${v}`)],
                              ["notes",          "Notes",      v => v || "—"],
                            ];
                            const diffs = fields.filter(([k]) => {
                              const a = before[k], b = after[k];
                              const norm = x => (x == null ? "" : String(x));
                              return norm(a) !== norm(b);
                            });
                            if (diffs.length === 0) {
                              return (
                                <div style={{ fontSize: "0.78rem", color: "#8a8070",
                                              fontStyle: "italic", marginBottom: 8 }}>
                                  No field-level diff — request may be a no-op.
                                </div>
                              );
                            }
                            return (
                              <div style={{ display: "grid", gap: 4, marginBottom: 8 }}>
                                {diffs.map(([k, label, fmt]) => (
                                  <div key={k} style={{
                                    display: "grid",
                                    gridTemplateColumns: "70px 1fr",
                                    gap: 6,
                                    fontSize: "0.76rem",
                                    background: "#fff",
                                    border: "1px solid #e7eef8",
                                    borderRadius: 6,
                                    padding: "5px 8px",
                                  }}>
                                    <span style={{ color: "#1d4fa8", fontWeight: 700,
                                                   textTransform: "uppercase",
                                                   fontSize: "0.66rem", letterSpacing: 0.4,
                                                   alignSelf: "center" }}>
                                      {label}
                                    </span>
                                    <span style={{ color: "#3d342a", wordBreak: "break-word" }}>
                                      <span style={{ color: "#a09080", textDecoration: "line-through" }}>
                                        {fmt(before[k])}
                                      </span>
                                      <span style={{ color: "#1d4fa8", margin: "0 6px" }}>→</span>
                                      <b style={{ color: "#1f6a2d" }}>{fmt(after[k])}</b>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                          {r.reason && (
                            <div style={{ fontSize: "0.78rem", color: "#3d342a",
                                          background: "#fff", padding: "6px 8px",
                                          borderRadius: 6, marginBottom: 8,
                                          borderLeft: "3px solid #c9a227" }}>
                              <span style={{ fontSize: "0.7rem", fontWeight: 700,
                                             color: "#8a8070", display: "block", marginBottom: 2 }}>
                                Your reason
                              </span>
                              {r.reason}
                            </div>
                          )}
                          {/* Admin's decision reason — only matters when REJECTED
                              (approval doesn't need a written explanation). */}
                          {r.status === "REJECTED" && r.decisionReason && (
                            <div style={{ fontSize: "0.78rem", color: "#3d342a",
                                          background: "#fdecea", padding: "6px 8px",
                                          borderRadius: 6, marginBottom: 8,
                                          borderLeft: "3px solid #c0392b" }}>
                              <span style={{ fontSize: "0.7rem", fontWeight: 700,
                                             color: "#a04030", display: "block", marginBottom: 2 }}>
                                Admin's reason for rejecting
                              </span>
                              {r.decisionReason}
                            </div>
                          )}
                          {/* Action buttons — only shown when the viewer
                              is authorised to act on THIS request's kind.
                              An Admin viewing an edit request sees no
                              buttons (Owner / SuperAdmin handle those);
                              a cashier never sees them at all. */}
                          {canActOnThis && r.status === "PENDING" && (
                            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                              <button
                                type="button"
                                onClick={() => rejectRequest(r)}
                                disabled={isBusy}
                                style={{
                                  padding: "5px 10px", fontSize: "0.78rem",
                                  background: "#fff", color: "#c0392b",
                                  border: "1px solid #e6b8b4", borderRadius: 6,
                                  cursor: isBusy ? "not-allowed" : "pointer",
                                  fontWeight: 600,
                                }}>
                                Reject
                              </button>
                              <button
                                type="button"
                                onClick={() => approveRequest(r)}
                                disabled={isBusy}
                                style={{
                                  padding: "5px 10px", fontSize: "0.78rem",
                                  background: "#1f6a2d", color: "#fff",
                                  border: "1px solid #1f6a2d", borderRadius: 6,
                                  cursor: isBusy ? "not-allowed" : "pointer",
                                  fontWeight: 700,
                                }}>
                                {isBusy ? "…" : "Approve"}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className={styles.userMenuWrap} ref={userMenuRef}>
          {(() => {
            // Pre-resolve so the same values drive the avatar fallback, the
            // dropdown header, and the native title tooltip. Keeps a single
            // source of truth for username / role across the collapsed pill
            // and the expanded panel.
            const photo = user?.photoUrl || user?.linkedEntity?.photoUrl || "";
            const displayName =
              user?.employeeName ||
              user?.linkedEntity?.displayName ||
              user?.username || "";
            const roleLabel = user?.role || "";
            // Student-only — resolved server-side at login (section →
            // grade level → name), so no extra fetch here. Right of the
            // STUDENT role badge was specifically requested so a student
            // (or their parent, viewing the parent account) can tell at a
            // glance which grade this account belongs to without having
            // to go find their section first.
            const gradeLevelLabel = user?.role === "Student"
              ? (user?.linkedEntity?.gradeLevelName || "")
              : "";
            const initial = (displayName || "?")[0].toUpperCase();
            return (
              <>
                <button
                  type="button"
                  className={styles.userInfo}
                  onClick={() => setUserMenuOpen(o => !o)}
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                  aria-label={`Account menu for ${displayName}`}
                  title={`${displayName}${roleLabel ? " · " + roleLabel : ""}`}>
                  {photo
                    ? <img src={photo} alt="" className={styles.userAvatarImg} />
                    : <div className={styles.userAvatar}>{initial}</div>}
                  <svg
                    className={`${styles.chevron} ${userMenuOpen ? styles.chevronOpen : ""}`}
                    width="10" height="10" viewBox="0 0 12 12" fill="none"
                    aria-hidden="true">
                    <path d="M2.5 4.5 L6 8 L9.5 4.5"
                          stroke="currentColor" strokeWidth="1.6"
                          strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {userMenuOpen && (
                  <div className={styles.userMenu} role="menu">
                    {/* Premium account header — large avatar, full name,
                        and a gold role-badge. Replaces the truncated
                        inline pill that used to live in the topbar. */}
                    <div className={styles.userMenuHeader}>
                      {photo
                        ? <img src={photo} alt="" className={styles.userMenuAvatarImg} />
                        : <div className={styles.userMenuAvatar}>{initial}</div>}
                      <div className={styles.userMenuIdentity}>
                        <div className={styles.userMenuName} title={displayName}>
                          {displayName}
                        </div>
                        {(roleLabel || gradeLevelLabel) && (
                          <div className={styles.userMenuBadgeRow}>
                            {roleLabel && (
                              <span className={styles.userMenuRoleBadge}
                                    title={roleLabel}>
                                {roleLabel}
                              </span>
                            )}
                            {gradeLevelLabel && (
                              <span className={styles.userMenuGradeBadge}
                                    title={`Grade level: ${gradeLevelLabel}`}>
                                {gradeLevelLabel}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles.userMenuDivider} aria-hidden="true" />
                    {isAdmin && (
                      <button
                        type="button"
                        className={styles.userMenuItem}
                        onClick={() => { setUserMenuOpen(false); nav("/settings"); }}
                        role="menuitem">
                        <span className={styles.userMenuIcon} aria-hidden="true">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="1.8"
                               strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="3" />
                            <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
                          </svg>
                        </span>
                        Settings
                      </button>
                    )}
                    <button
                      type="button"
                      className={`${styles.userMenuItem} ${styles.userMenuItemDanger}`}
                      onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                      role="menuitem">
                      <span className={styles.userMenuIcon} aria-hidden="true">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="1.8"
                             strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                          <polyline points="16 17 21 12 16 7"/>
                          <line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                      </span>
                      Logout
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {detailsRequest && (
        <ApprovalDetailsModal
          request={detailsRequest}
          // Approve/Reject affordances only when the viewer can act on this
          // kind (delete = Admin/SuperAdmin, edit = Owner/SuperAdmin) AND
          // the row is still pending. Cashier view is read-only.
          canDecide={
               detailsRequest.status === "PENDING"
            && ((detailsRequest._kind === "edit" && canApproveEdits)
                || (detailsRequest._kind !== "edit" && canApproveDeletes))
          }
          onClose={() => setDetailsRequest(null)}
          onApprove={async () => {
            await approveRequest(detailsRequest);
            setDetailsRequest(null);
          }}
          onReject={() => {
            // Open the styled reject dialog instead of inline prompt; the
            // details modal stays open behind it so context isn't lost.
            rejectRequest(detailsRequest);
          }}
          busy={busyId === detailsRequest.id}
        />
      )}
      {/* Styled reject dialog — used by both delete and edit requests in
          this topbar inbox. The reason field is required; on confirm we
          POST to the right endpoint based on the target's _kind. */}
      <RejectReasonDialog
        open={!!rejectTarget}
        title={rejectTarget?._kind === "edit"
          ? "Reject edit request"
          : "Reject delete request"}
        description={rejectTarget
          ? `${(rejectTarget.student?.lastName || rejectTarget.student?.firstName)
              ? `${rejectTarget.student?.lastName || ""}, ${rejectTarget.student?.firstName || ""}`.replace(/^, |, $/g, "")
              : "Payment"} · ₱${fmtMoney(rejectTarget.payment?.amount)}`
          : ""}
        placeholder={rejectTarget?._kind === "edit"
          ? 'e.g. "Amount differs from the receipt — please verify and re-submit."'
          : 'e.g. "Reason given is insufficient — keep the payment for now."'}
        busy={busyId === rejectTarget?.id}
        onConfirm={confirmReject}
        onCancel={() => { if (busyId !== rejectTarget?.id) setRejectTarget(null); }}
      />
    </div>
  );
}

/** Full-detail popup for one pending payment-deletion request. Pulled out
 *  of Topbar so the dense dropdown card stays compact and the modal can
 *  surface every field the backend's enrich() returns — payment provenance
 *  (who recorded it, notes, ref/bank), invoice context (current amount,
 *  status, due date), and the request audit trail. */
function ApprovalDetailsModal({ request, onClose, onApprove, onReject, busy, canDecide = true }) {
  const r   = request;
  const p   = r.payment || {};
  const inv = r.invoice || {};
  const stu = r.student || {};
  const studentName = (stu.lastName || stu.firstName)
    ? `${stu.lastName || ""}, ${stu.firstName || ""}${stu.middleName ? " " + stu.middleName : ""}`
        .replace(/^, |, $/g, "")
    : "—";

  // Plain-CSS modal — Topbar is a leaf component and doesn't import any
  // shared modal stylesheet, so this keeps the styling self-contained.
  const overlay = {
    position: "fixed", inset: 0, background: "rgba(0, 0, 0, 0.45)",
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    paddingTop: "8vh", zIndex: 1000,
  };
  const card = {
    width: "min(560px, 92vw)", maxHeight: "84vh", overflowY: "auto",
    background: "#fff", borderRadius: 12,
    boxShadow: "0 12px 32px rgba(0,0,0,0.25)",
  };
  const head = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 16px", borderBottom: "1px solid #f0ebe0",
  };
  const sectionTitle = {
    fontSize: "0.7rem", fontWeight: 800, letterSpacing: 0.6,
    textTransform: "uppercase", color: "#a39880",
    padding: "12px 16px 4px",
  };
  const kvRow = {
    display: "grid", gridTemplateColumns: "140px 1fr",
    padding: "4px 16px", fontSize: "0.85rem", color: "#3d342a",
  };
  const kvLabel = { color: "#8a8070" };

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  return (
    <div style={overlay}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={head}>
          <div style={{ fontWeight: 800, color: "#3d342a" }}>
            Delete request — ₱{fmtMoney(p.amount)}
          </div>
          <button type="button" onClick={onClose}
                  aria-label="Close"
                  style={{
                    background: "transparent", border: "none",
                    fontSize: 20, cursor: "pointer", color: "#8a8070",
                    lineHeight: 1, padding: 4,
                  }}>×</button>
        </div>

        <div style={sectionTitle}>Payment</div>
        <div style={kvRow}><span style={kvLabel}>Payment #</span><span>{p.id ?? "—"}</span></div>
        <div style={kvRow}><span style={kvLabel}>Amount</span><span style={{ fontWeight: 800, color: "#1f6a2d" }}>₱{fmtMoney(p.amount)}</span></div>
        <div style={kvRow}><span style={kvLabel}>Method</span><span>{p.method || "—"}{p.bankName ? ` · ${p.bankName}` : ""}</span></div>
        <div style={kvRow}><span style={kvLabel}>Reference #</span><span>{p.referenceNo || <em style={{ color: "#a39880" }}>none</em>}</span></div>
        <div style={kvRow}><span style={kvLabel}>Paid at</span><span>{p.paidAt ? new Date(p.paidAt).toLocaleString() : "—"}</span></div>
        <div style={kvRow}><span style={kvLabel}>Recorded by</span>
          <span>
            {p.recordedByUsername || "—"}
            {p.recordedByRole ? ` · ${p.recordedByRole}` : ""}
            {p.receivedByStaffId ? ` (staff #${p.receivedByStaffId})` : ""}
          </span>
        </div>
        {p.appliesToMonth != null && (
          <div style={kvRow}><span style={kvLabel}>Applies to</span>
            <span>{p.appliesToMonth === 0 ? "Enrollment" : `Month ${p.appliesToMonth}`}</span>
          </div>
        )}
        {p.particular && (
          <div style={kvRow}><span style={kvLabel}>Particular</span><span>{p.particular}</span></div>
        )}
        {p.notes && (
          <div style={kvRow}><span style={kvLabel}>Notes</span><span>{p.notes}</span></div>
        )}

        <div style={sectionTitle}>Student</div>
        <div style={kvRow}><span style={kvLabel}>Name</span><span>{studentName}</span></div>
        <div style={kvRow}><span style={kvLabel}>Student #</span><span>{stu.studentNumber || "—"}</span></div>
        <div style={kvRow}><span style={kvLabel}>Student id</span><span>{stu.id ?? "—"}</span></div>

        <div style={sectionTitle}>Invoice</div>
        <div style={kvRow}><span style={kvLabel}>Invoice #</span><span>{inv.id ?? "—"}</span></div>
        <div style={kvRow}><span style={kvLabel}>Invoice amount</span>
          <span>{inv.amount != null ? `₱${fmtMoney(inv.amount)}` : "—"}</span>
        </div>
        <div style={kvRow}><span style={kvLabel}>Invoice status</span>
          <span style={{ fontWeight: 700, color: inv.status === "PAID" ? "#1f6a2d" : "#3d342a" }}>
            {inv.status || "—"}
          </span>
        </div>
        <div style={kvRow}><span style={kvLabel}>Due date</span><span>{inv.dueDate || "—"}</span></div>

        <div style={sectionTitle}>Request</div>
        <div style={kvRow}><span style={kvLabel}>Status</span>
          <span style={{
            display: "inline-block", padding: "1px 6px",
            fontSize: "0.7rem", fontWeight: 800, letterSpacing: 0.4,
            textTransform: "uppercase", borderRadius: 4,
            background: r.status === "APPROVED" ? "#e3f2e6"
                      : r.status === "REJECTED" ? "#fdecea" : "#fff8e1",
            color:      r.status === "APPROVED" ? "#1f6a2d"
                      : r.status === "REJECTED" ? "#c0392b" : "#8a6a1c",
            border: "1px solid",
            borderColor: r.status === "APPROVED" ? "#bcdcc4"
                       : r.status === "REJECTED" ? "#e6b8b4" : "#efe1a8",
          }}>
            {r.status || "—"}
          </span>
        </div>
        <div style={kvRow}><span style={kvLabel}>Requested by</span>
          <span>
            <b>{r.requestedByUsername || "—"}</b>
            {r.requestedByRole ? ` · ${r.requestedByRole}` : ""}
          </span>
        </div>
        <div style={kvRow}><span style={kvLabel}>When</span>
          <span>{r.createdAt ? new Date(r.createdAt).toLocaleString() : "—"}</span>
        </div>
        <div style={{ ...kvRow, alignItems: "flex-start" }}>
          <span style={kvLabel}>Reason</span>
          <span style={{
            background: "#fff8e1", borderLeft: "3px solid #c9a227",
            padding: "6px 8px", borderRadius: 4,
          }}>
            {r.reason || <em style={{ color: "#a39880" }}>(none)</em>}
          </span>
        </div>
        {r.status !== "PENDING" && (
          <>
            <div style={kvRow}><span style={kvLabel}>Decided by</span>
              <span>
                <b>{r.reviewedByUsername || "—"}</b>
                {r.reviewedAt ? ` · ${new Date(r.reviewedAt).toLocaleString()}` : ""}
              </span>
            </div>
            {r.decisionReason && (
              <div style={{ ...kvRow, alignItems: "flex-start" }}>
                <span style={kvLabel}>Decision reason</span>
                <span style={{
                  background: r.status === "REJECTED" ? "#fdecea" : "#e3f2e6",
                  borderLeft: `3px solid ${r.status === "REJECTED" ? "#c0392b" : "#1f6a2d"}`,
                  padding: "6px 8px", borderRadius: 4,
                }}>
                  {r.decisionReason}
                </span>
              </div>
            )}
          </>
        )}

        <div style={{
          display: "flex", gap: 8, justifyContent: "flex-end",
          padding: 16, borderTop: "1px solid #f0ebe0", marginTop: 8,
        }}>
          <button type="button" onClick={onClose} disabled={busy}
                  style={{
                    padding: "8px 14px", fontSize: "0.85rem",
                    background: "#fff", color: "#3d342a",
                    border: "1px solid #d6cdb8", borderRadius: 6,
                    cursor: busy ? "not-allowed" : "pointer",
                  }}>
            Close
          </button>
          {canDecide && (
            <>
              <button type="button" onClick={onReject} disabled={busy}
                      style={{
                        padding: "8px 14px", fontSize: "0.85rem",
                        background: "#fff", color: "#c0392b",
                        border: "1px solid #e6b8b4", borderRadius: 6,
                        cursor: busy ? "not-allowed" : "pointer",
                        fontWeight: 600,
                      }}>
                Reject
              </button>
              <button type="button" onClick={onApprove} disabled={busy}
                      style={{
                        padding: "8px 14px", fontSize: "0.85rem",
                        background: "#1f6a2d", color: "#fff",
                        border: "1px solid #1f6a2d", borderRadius: 6,
                        cursor: busy ? "not-allowed" : "pointer",
                        fontWeight: 700,
                      }}>
                {busy ? "…" : "Approve"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
